import Axios from './axiosClient'
import { API_ENDPOINT } from '../constants/apis'
import { AxiosResponse } from 'axios'
import type {
  PaginatedResponse,
  Organization,
  OrganizationDetails,
  OrganizationSensor,
  OrgAiIntegration,
  OrgAiIntegrationUsage,
  OrgAiIntegrationExplorer,
  AiIntegrationToken,
  GitlabTokenName,
  AiBackfillTriggerResponse,
  AiBackfillResult,
  AiChatHistory,
  AiChatCount,
  AiChatSessionDetail,
  AgenticChatHistory,
  AgenticChatSessionDetail,
  CustomDashboard,
  OrganizationAccount,
  CustomDashboardDetail,
  WidgetExecutionResult,
  OrganizationSetting,
  GetOrganizationsParams,
  OrgHealthScore,
  OrgInvoices
} from '../types'

export const getOrganizations = ({
  page = 1,
  limit = 50,
  searchParam = null,
  featureFlagParam = null,
  featureFlagInverted = false,
  isPaid = true,
  isTrial = false,
  isActive = 'true',
  sortKey = null,
  sortDirection,
  planName = null,
  sensorName = null,
  sensorMethod = null,
  isOpa = null,
  searchUsers = false,
  signal
}: GetOrganizationsParams = {}): Promise<
  AxiosResponse<PaginatedResponse<Organization>>
> => {
  return Axios.get(`${API_ENDPOINT}/organizations/summary`, {
    params: {
      page: page,
      limit: limit,
      search_param: searchParam,
      feature_flag_param: featureFlagParam,
      feature_flag_inverted: featureFlagParam ? featureFlagInverted : undefined,
      is_paid: isPaid,
      is_trial: isTrial,
      is_active: isActive,
      sort_key: sortKey || undefined,
      sort_direction: sortDirection || undefined,
      plan_name: planName || undefined,
      sensor_name: sensorName || undefined,
      sensor_method: sensorMethod || undefined,
      is_opa: isOpa ?? undefined,
      search_users: searchUsers
    },
    signal
  })
}

export const getOrganizationsByIds = (
  orgIds: (string | number)[],
  {
    page = 1,
    limit = 50,
    searchParam = null,
    sortKey = null,
    sortDirection,
    searchUsers = false
  }: {
    page?: number
    limit?: number
    searchParam?: string | null
    sortKey?: string | null
    sortDirection?: 'asc' | 'desc'
    searchUsers?: boolean
  } = {},
  signal?: AbortSignal
): Promise<AxiosResponse<PaginatedResponse<Organization>>> => {
  return Axios.get(`${API_ENDPOINT}/organizations/by-ids`, {
    params: {
      ids: orgIds.join(','),
      page,
      limit,
      search_param: searchParam,
      sort_key: sortKey || undefined,
      sort_direction: sortDirection || undefined,
      search_users: searchUsers
    },
    signal
  })
}

export const getOrganizationDetails = (
  organizationId: string | number
): Promise<AxiosResponse<OrganizationDetails>> => {
  return Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/details`)
}

export const invalidateOrganizationDetailsCache = (
  organizationId: string | number
): Promise<AxiosResponse<{ ok: boolean }>> => {
  return Axios.delete(
    `${API_ENDPOINT}/organizations/${organizationId}/details-cache`
  )
}

export const getOrganizationSensors = (
  organizationId: string | number
): Promise<AxiosResponse<OrganizationSensor[]>> => {
  return Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/sensors`)
}

export const getOrganizationAiIntegrations = (
  organizationId: string | number
): Promise<AxiosResponse<OrgAiIntegration[]>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations`
  )
}

export const getOrganizationAiIntegrationUsage = (
  organizationId: string | number,
  sensorAuthorizationId: string | number,
  start: string,
  end: string,
  user?: string,
  team?: string,
  granularity?: string,
  snapshot?: boolean
): Promise<AxiosResponse<OrgAiIntegrationUsage>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/${sensorAuthorizationId}/usage`,
    {
      params: {
        start,
        end,
        ...(user ? { user } : {}),
        ...(team ? { team } : {}),
        ...(granularity ? { granularity } : {}),
        ...(snapshot ? { snapshot } : {})
      }
    }
  )
}

// Totals + models only, for backfill before/after checks: the full usage report
// times out on large orgs.
export const getOrganizationAiIntegrationUsageSnapshot = (
  organizationId: string | number,
  sensorAuthorizationId: string | number,
  start: string,
  end: string
): Promise<AxiosResponse<OrgAiIntegrationUsage>> =>
  getOrganizationAiIntegrationUsage(
    organizationId,
    sensorAuthorizationId,
    start,
    end,
    undefined,
    undefined,
    undefined,
    true
  )

export const getOrganizationAiIntegrationExplorer = (
  organizationId: string | number,
  sensorAuthorizationId: string | number,
  start: string,
  end: string
): Promise<AxiosResponse<OrgAiIntegrationExplorer>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/${sensorAuthorizationId}/explorer`,
    {
      params: { start, end }
    }
  )
}

export const triggerAiBackfill = (
  organizationId: string | number,
  sensorAuthorizationId: string | number,
  since: string,
  until: string
): Promise<AxiosResponse<AiBackfillTriggerResponse>> => {
  return Axios.post(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/${sensorAuthorizationId}/backfill`,
    { since, until }
  )
}

export const revealAiIntegrationToken = (
  organizationId: string | number,
  sensorAuthorizationId: string | number
): Promise<AxiosResponse<AiIntegrationToken>> => {
  return Axios.post(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/${sensorAuthorizationId}/token`
  )
}

export const getGitlabTokenName = (
  organizationId: string | number,
  sensorAuthorizationId: string | number
): Promise<AxiosResponse<GitlabTokenName>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/sensors/${sensorAuthorizationId}/token-name`
  )
}

export const saveBackfillResult = (
  organizationId: string | number,
  sensorAuthorizationId: string | number,
  jobsSuiteUuid: string,
  result: Partial<AiBackfillResult>
): Promise<AxiosResponse<{ status: string }>> => {
  return Axios.post(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/${sensorAuthorizationId}/backfill/${jobsSuiteUuid}/result`,
    result
  )
}

export const getBackfillResult = (
  organizationId: string | number,
  sensorAuthorizationId: string | number,
  jobsSuiteUuid: string
): Promise<AxiosResponse<AiBackfillResult>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/${sensorAuthorizationId}/backfill/${jobsSuiteUuid}/result`
  )
}

export const getBackfillHistory = (
  organizationId: string | number
): Promise<AxiosResponse<{ results: AiBackfillResult[] }>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/ai-integrations/backfills`
  )
}

export const getOrganizationAiChatHistory = (
  organizationId: string | number,
  signal?: AbortSignal
): Promise<AxiosResponse<AiChatHistory>> => {
  return Axios.post(
    `${API_ENDPOINT}/lookup/ai-chat-history`,
    { organization_id: Number(organizationId) },
    { signal }
  )
}

export const getOrganizationAiChatCount = (
  organizationId: string | number,
  signal?: AbortSignal
): Promise<AxiosResponse<AiChatCount>> => {
  return Axios.post(
    `${API_ENDPOINT}/lookup/ai-chat-count`,
    { organization_id: Number(organizationId) },
    { signal }
  )
}

export const getOrganizationAiChatSession = (
  organizationId: string | number,
  sessionKey: string,
  signal?: AbortSignal
): Promise<AxiosResponse<AiChatSessionDetail>> => {
  return Axios.post(
    `${API_ENDPOINT}/lookup/ai-chat-session`,
    { organization_id: Number(organizationId), session_key: sessionKey },
    { signal }
  )
}

export const getOrganizationAgenticChatHistory = (
  organizationId: string | number,
  signal?: AbortSignal
): Promise<AxiosResponse<AgenticChatHistory>> => {
  return Axios.post(
    `${API_ENDPOINT}/lookup/ai-chat-history-v2`,
    { organization_id: Number(organizationId) },
    { signal }
  )
}

export const getOrganizationAgenticChatSession = (
  organizationId: string | number,
  sessionId: string,
  signal?: AbortSignal
): Promise<AxiosResponse<AgenticChatSessionDetail>> => {
  return Axios.post(
    `${API_ENDPOINT}/lookup/ai-chat-session-v2`,
    { organization_id: Number(organizationId), session_id: sessionId },
    { signal }
  )
}

export const getOrganizationCustomDashboards = (
  organizationId: string | number,
  signal?: AbortSignal
): Promise<AxiosResponse<CustomDashboard[]>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/custom-dashboards`,
    { signal }
  )
}

export const getOrganizationAccounts = (
  organizationId: string | number,
  search: string,
  adminsOnly: boolean,
  signal?: AbortSignal
): Promise<AxiosResponse<PaginatedResponse<OrganizationAccount>>> => {
  return Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/accounts`, {
    params: { search: search || undefined, admins_only: adminsOnly },
    signal
  })
}

export const getOrganizationCustomDashboard = (
  organizationId: string | number,
  dashboardId: string | number,
  signal?: AbortSignal
): Promise<AxiosResponse<CustomDashboardDetail>> => {
  return Axios.get(
    `${API_ENDPOINT}/organizations/${organizationId}/custom-dashboards/${dashboardId}`,
    { signal }
  )
}

export const executeCustomDashboardWidget = (
  organizationId: string | number,
  dashboardId: string | number,
  widgetId: string,
  parameters: Record<string, string>,
  targetOrganizationId?: string,
  signal?: AbortSignal
): Promise<AxiosResponse<WidgetExecutionResult>> => {
  return Axios.post(
    `${API_ENDPOINT}/organizations/${organizationId}/custom-dashboards/${dashboardId}/widgets/${widgetId}/execute`,
    { parameters, target_organization_id: targetOrganizationId || undefined },
    { signal }
  )
}

export const executeOrganizationSql = (
  organizationId: string | number,
  sql: string,
  parameters: Record<string, string>
): Promise<AxiosResponse<WidgetExecutionResult>> => {
  return Axios.post(
    `${API_ENDPOINT}/organizations/${organizationId}/query/execute`,
    { sql, parameters }
  )
}

export const getOrganizationSettings = (
  organizationId: string | number
): Promise<AxiosResponse<OrganizationSetting[]>> => {
  return Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/settings`)
}

export const updateOrganizationSettings = (
  organizationId: string | number,
  payload: any
): Promise<AxiosResponse<any>> => {
  return Axios.put(
    `${API_ENDPOINT}/organizations/${organizationId}/settings`,
    payload
  )
}

export const updateOrganizationFeatureFlags = (
  organizationId: string | number,
  payload: any
): Promise<AxiosResponse<any>> => {
  return Axios.put(
    `${API_ENDPOINT}/organizations/${organizationId}/feature_flags`,
    payload
  )
}

export const setOrganizationSupportAccount = (
  organizationId: string | number
): Promise<AxiosResponse<any>> =>
  Axios.put(`${API_ENDPOINT}/organizations/${organizationId}/support`)

export const getOrganizationsByProviderOrg = (
  search: string,
  {
    page = 1,
    limit = 50
  }: {
    page?: number
    limit?: number
  } = {},
  signal?: AbortSignal
): Promise<AxiosResponse<{ items: any[]; total_count: number }>> =>
  Axios.get(`${API_ENDPOINT}/organizations/by-provider-org`, {
    params: {
      search,
      page,
      limit
    },
    signal
  })

export const getOrganizationsByUser = (
  search: string,
  {
    page = 1,
    limit = 50
  }: {
    page?: number
    limit?: number
  } = {},
  signal?: AbortSignal
): Promise<AxiosResponse<{ items: any[]; total_count: number }>> =>
  Axios.get(`${API_ENDPOINT}/organizations/by-user`, {
    params: {
      search,
      page,
      limit
    },
    signal
  })

export const getOrganizationRecentPrs = (
  organizationId: string | number,
  {
    page = 1,
    limit = 10,
    state = null,
    repoName = null,
    authorName = null
  }: {
    page?: number
    limit?: number
    state?: string | null
    repoName?: string | null
    authorName?: string | null
  } = {},
  signal?: AbortSignal
): Promise<AxiosResponse<{ items: any[]; total_count: number }>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/recent_prs`, {
    params: {
      page,
      limit,
      state: state || undefined,
      repo_name: repoName || undefined,
      author_name: authorName || undefined
    },
    signal
  })

export interface GitstreamRunItem {
  pr_url: string | null
  repository: string | null
  owner: string | null
  provider: string | null
  run_count: number
  total_credits: number
  first_run_at: string | null
  last_run_at: string | null
}

export const getOrganizationGitstreamRuns = (
  organizationId: string | number,
  {
    page = 1,
    limit = 10,
    fromTs = null,
    toTs = null
  }: {
    page?: number
    limit?: number
    fromTs?: number | null
    toTs?: number | null
  } = {},
  signal?: AbortSignal
): Promise<AxiosResponse<{ items: GitstreamRunItem[]; total_count: number }>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/gitstream-runs`, {
    params: {
      offset: (page - 1) * limit,
      limit,
      from_ts: fromTs ?? undefined,
      to_ts: toTs ?? undefined
    },
    signal
  })

export type InsightSeverity = 'info' | 'warning' | 'critical'
export type InsightGroup = 'call_to_action' | 'did_you_know'

export interface InsightMetric {
  label: string
  value: number
}

export interface Insight {
  id: string
  category: string
  group: InsightGroup
  severity: InsightSeverity
  title: string
  message: string
  metric?: InsightMetric
  metadata?: Record<string, unknown>
}

export interface OrgInsightsResponse {
  items: Insight[]
  total_count: number
  generated_at: string
}

export const getOrganizationInsights = (
  organizationId: string | number,
  refresh: boolean = false
): Promise<AxiosResponse<OrgInsightsResponse>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/insights`, {
    params: { refresh: refresh || undefined }
  })

export interface OrgHasInsightsResponse {
  has_insights: boolean
  count: number
  has_buyers: boolean
  buyer_count: number
  generated_at: string | null
  // True when the lookup timed out: unknown yet, not "no insights"
  pending?: boolean
}

export const getOrganizationHasInsights = (
  organizationId: string | number,
  signal?: AbortSignal
): Promise<AxiosResponse<OrgHasInsightsResponse>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/has-insights`, {
    signal
  })

export interface OrgBuyer {
  contact_id: string
  contact_name: string | null
  title: string | null
  email: string
}

export interface OrgBuyersResponse {
  items: OrgBuyer[]
  total_count: number
  generated_at: string
}

export const getOrganizationBuyers = (
  organizationId: string | number
): Promise<AxiosResponse<OrgBuyersResponse>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/buyers`)

export type OrgUsagePeriod = {
  logged_in_users: number
  logins: number
  active_users: number
  dashboard_viewers: number
  dashboard_views: number
  dashboards_created: number
  ai_assistant_users: number
  ai_assistant_messages: number
  ai_reviewed_prs: number
  ai_code_reviews: number
  ai_pr_descriptions: number
  api_calls: number
  mcp_calls: number
}

export type OrgUsagePeriodKey = '7d' | '30d' | '90d'

export type OrgUsageDay = OrgUsagePeriod & { date: string }

export type OrgUsageResponse = {
  periods: Partial<Record<OrgUsagePeriodKey, OrgUsagePeriod>>
  daily?: OrgUsageDay[]
  generated_at: string
}

export const getOrganizationUsage = (
  organizationId: string | number
): Promise<AxiosResponse<OrgUsageResponse>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/usage`)

export const getOrganizationHealthScore = (
  organizationId: string | number
): Promise<AxiosResponse<OrgHealthScore>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/health-score`)

export const getOrganizationInvoices = (
  organizationId: string | number
): Promise<AxiosResponse<OrgInvoices>> =>
  Axios.get(`${API_ENDPOINT}/organizations/${organizationId}/invoices`)
